import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { CartProvider } from './hooks/useCart';
import { BrandingProvider } from './hooks/useBranding';

import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import WorkOrderPage from './pages/WorkOrderPage';
import CatalogPage from './pages/CatalogPage';
import CartPage from './pages/CartPage';
import ProductsAdminPage from './pages/admin/ProductsAdminPage';
import CustomersAdminPage from './pages/admin/CustomersAdminPage';
import UsersAdminPage from './pages/admin/UsersAdminPage';
import RoleManagerPage from './pages/admin/RoleManagerPage';
import AddressesAdminPage from './pages/admin/AddressesAdminPage';
import BrandingAdminPage from './pages/admin/BrandingAdminPage';
import EmailSettingsPage from './pages/admin/EmailSettingsPage';
import SettingsPage from './pages/admin/SettingsPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import ImportsPage from './pages/admin/ImportsPage';
import BulkImagesPage from './pages/admin/BulkImagesPage';
import GroupsAdminPage from './pages/admin/GroupsAdminPage';
import AttributeBuilderPage from './pages/admin/AttributeBuilderPage';
import ChargeCatalogPage from './pages/admin/ChargeCatalogPage';

function PrivateRoute({ children, roles }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();

  // Wait for auth check to complete before rendering any routes.
  // Without this, the login page loads, calls /auth/me, gets a 401,
  // and the old axios interceptor would redirect back to /login — infinite loop.
  if (user === undefined) return <div className="loading">Loading…</div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/:id" element={<OrderDetailPage />} />
        <Route path="orders/:id/work-order" element={
          <PrivateRoute roles={['ttc_superadmin','ttc_staff']}><WorkOrderPage /></PrivateRoute>
        } />
        <Route path="catalog" element={<CatalogPage />} />
        <Route path="cart" element={<CartPage />} />
        <Route path="admin/products" element={
          <PrivateRoute roles={['ttc_superadmin','ttc_staff']}><ProductsAdminPage /></PrivateRoute>
        } />
        <Route path="admin/attributes" element={
          <PrivateRoute roles={['ttc_superadmin','ttc_staff']}><AttributeBuilderPage /></PrivateRoute>
        } />
        <Route path="admin/charges" element={
          <PrivateRoute roles={['ttc_superadmin','ttc_staff']}><ChargeCatalogPage /></PrivateRoute>
        } />
        <Route path="admin/bulk-images" element={
          <PrivateRoute roles={['ttc_superadmin','ttc_staff']}><BulkImagesPage /></PrivateRoute>
        } />
        <Route path="admin/imports" element={
          <PrivateRoute roles={['ttc_superadmin','ttc_staff']}><ImportsPage /></PrivateRoute>
        } />
        <Route path="admin/customers" element={
          <PrivateRoute roles={['ttc_superadmin','ttc_staff']}><CustomersAdminPage /></PrivateRoute>
        } />
        <Route path="admin/users" element={
          <PrivateRoute roles={['ttc_superadmin','ttc_staff','customer_admin']}><UsersAdminPage /></PrivateRoute>
        } />
        <Route path="admin/roles" element={
          <PrivateRoute roles={['ttc_superadmin']}><RoleManagerPage /></PrivateRoute>
        } />
        <Route path="admin/audit" element={
          <PrivateRoute roles={['ttc_superadmin']}><AuditLogPage /></PrivateRoute>
        } />
        <Route path="admin/settings" element={
          <PrivateRoute roles={['ttc_superadmin']}><SettingsPage /></PrivateRoute>
        } />
        <Route path="admin/email" element={
          <PrivateRoute roles={['ttc_superadmin']}><EmailSettingsPage /></PrivateRoute>
        } />
        <Route path="admin/branding" element={
          <PrivateRoute roles={['ttc_superadmin']}><BrandingAdminPage /></PrivateRoute>
        } />
        <Route path="admin/addresses" element={
          <PrivateRoute roles={['ttc_superadmin','ttc_staff','customer_admin']}><AddressesAdminPage /></PrivateRoute>
        } />
        <Route path="admin/groups" element={
          <PrivateRoute roles={['ttc_superadmin','ttc_staff','customer_admin']}><GroupsAdminPage /></PrivateRoute>
        } />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandingProvider>
          <CartProvider>
            <AppRoutes />
            <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
          </CartProvider>
        </BrandingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
